/**
 * CS Client Profile Modal
 *
 * Displays client PII information in a read-only modal.
 * Shows personal data from the Account record linked to the CS Audit.
 */

import React from 'react';
import { X, User, ExternalLink } from 'lucide-react';
import { StandardButton } from '../standard';

export interface ClientProfileData {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  currentAddress?: string;
  previousAddresses?: string[];
  aliases?: string[];
  emails?: string[];
  phones?: string[];
  ssnLast4?: string;
}

interface CSClientProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientData: ClientProfileData;
  onViewFullRecord: () => void;
}

const CSClientProfileModal: React.FC<CSClientProfileModalProps> = ({
  isOpen,
  onClose,
  clientData,
  onViewFullRecord,
}) => {
  if (!isOpen) return null;

  // Calculate age from DOB
  const calculateAge = (dob: string): number | null => {
    if (!dob) return null;
    try {
      const birthDate = new Date(dob);
      if (isNaN(birthDate.getTime())) return null;
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch {
      return null;
    }
  };

  // Format DOB for display
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const age = clientData.dateOfBirth ? calculateAge(clientData.dateOfBirth) : null;

  // Table row component for consistency
  const ProfileRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <tr className="border-b border-gray-100 dark:border-gray-700 last:border-0">
      <td className="py-3 pr-4 text-sm font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap w-40">
        {label}
      </td>
      <td className="py-3 text-sm text-gray-900 dark:text-gray-100">
        {value || '—'}
      </td>
    </tr>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Client Profile
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {clientData.name || 'Unknown Client'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            <table className="w-full">
              <tbody>
                <ProfileRow label="Full Name" value={clientData.name} />
                <ProfileRow
                  label="Date of Birth"
                  value={clientData.dateOfBirth ? formatDate(clientData.dateOfBirth) : null}
                />
                <ProfileRow
                  label="Age"
                  value={age !== null ? `${age} years` : null}
                />
                <ProfileRow label="Current Address" value={clientData.currentAddress} />
                <ProfileRow
                  label="Previous Addresses"
                  value={
                    clientData.previousAddresses && clientData.previousAddresses.length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {clientData.previousAddresses.map((addr, idx) => (
                          <li key={idx}>{addr}</li>
                        ))}
                      </ul>
                    ) : null
                  }
                />
                <ProfileRow
                  label="Aliases / Other Names"
                  value={
                    clientData.aliases && clientData.aliases.length > 0
                      ? clientData.aliases.join(', ')
                      : null
                  }
                />
                <ProfileRow
                  label="Email Addresses"
                  value={
                    clientData.emails && clientData.emails.length > 0 ? (
                      <ul className="space-y-1">
                        {clientData.emails.map((email, idx) => (
                          <li key={idx}>
                            <a
                              href={`mailto:${email}`}
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {email}
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : null
                  }
                />
                <ProfileRow
                  label="Phone Numbers"
                  value={
                    clientData.phones && clientData.phones.length > 0
                      ? clientData.phones.join(', ')
                      : null
                  }
                />
                <ProfileRow
                  label="SSN (Last 4)"
                  value={
                    clientData.ssnLast4 ? (
                      <span className="font-mono">XXX-XX-{clientData.ssnLast4}</span>
                    ) : null
                  }
                />
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-lg">
            <StandardButton
              variant="secondary"
              leftIcon={<ExternalLink className="h-4 w-4" />}
              onClick={onViewFullRecord}
            >
              View Full Record
            </StandardButton>
            <StandardButton variant="primary" onClick={onClose}>
              Close
            </StandardButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSClientProfileModal;
